/**
 * @file use-confirm.ts
 * @description Подтверждение действия окном кабинета вместо системного
 * `confirm()` (phase19.31; поднято из зоны «Склад», `use-wms-confirm`, по
 * ревью: нужно и складу, и карточке клиента).
 *
 * Хук отдаёт `confirm(request)` — обещание «да / нет», как у `window.confirm`,
 * поэтому места вызова почти не меняются (`if (!(await confirm(...))) return`).
 * Окно рисует `ConfirmDialog` по `request` и закрывает его через `settle`.
 * Системное окно браузера не похоже на кабинет, на телефоне мелкое и без
 * перевода кнопок (находка приёмки склада).
 *
 * Выбор из двух действий (Р3г: «Продолжить черновик» / «Начать новую») —
 * `confirmChoice`: `true` / `false` по кнопкам, `null` — окно закрыли (Esc)
 * без выбора. У `confirm` закрытие по-прежнему = `false`.
 */

import { useCallback, useRef, useState } from "react";

/** Что спросить: заголовок, пояснение и подпись кнопки действия. */
export interface ConfirmRequest {
  title: string;
  description?: string;
  /** Подпись кнопки «да»: «Провести», «Удалить». */
  action: string;
  /** Опасное действие — красная кнопка окончательного подтверждения. */
  danger?: boolean;
  /** Подпись кнопки «нет», если это не просто отмена: «Продолжить черновик». */
  cancel?: string;
}

export interface ConfirmApi {
  /** Открыть окно; `true` — человек подтвердил. */
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  /** Выбор из двух действий: `true` — действие, `false` — `cancel`, `null` — закрыли без выбора. */
  confirmChoice: (request: ConfirmRequest) => Promise<boolean | null>;
  /** Окно закрыли без выбора (Esc): `confirmChoice` → `null`, `confirm` → `false`. */
  dismiss: () => void;
  /** Открытый вопрос; `null` — окно закрыто. */
  request: ConfirmRequest | null;
  /** Ответ из окна. */
  settle: (ok: boolean) => void;
}

export function useConfirm(): ConfirmApi {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  // Резолвер обещания — в ref: он не влияет на отрисовку.
  const resolver = useRef<((ok: boolean | null) => void) | null>(null);

  const confirmChoice = useCallback((next: ConfirmRequest) => {
    // Прежний вопрос без ответа считаем закрытым без выбора — обещание не должно висеть.
    resolver.current?.(null);
    setRequest(next);
    return new Promise<boolean | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  // Прежний контракт: закрытие без выбора — отказ.
  const confirm = useCallback((next: ConfirmRequest) => confirmChoice(next).then((ok) => ok === true), [confirmChoice]);

  const answer = useCallback((ok: boolean | null) => {
    resolver.current?.(ok);
    resolver.current = null;
    setRequest(null);
  }, []);

  const settle = useCallback((ok: boolean) => answer(ok), [answer]);
  const dismiss = useCallback(() => answer(null), [answer]);

  return { confirm, confirmChoice, dismiss, request, settle };
}
